import Hotkeys from 'react-hot-keys';
import { motion } from 'framer-motion';
import ModalDialog from '../../../components/common/ModalDialog';
import { Checkbox, Col, notification } from 'antd';
import { useState } from 'react';
import { DEFAULT_CONFIG_ELEMENT_IN_DOUBLE_COLUMNS } from '../../../constants';
import { ColumnConfig, TemplateConfig } from '../../../Templar/RenderPdfPrescription';

const DoubleColumnConfigModal: React.FC<{
    rxConfig: TemplateConfig | undefined;
    saveRxConfigInState: (rxObj: TemplateConfig) => void;
    closeConfig: () => void;
}> = ({ rxConfig, saveRxConfigInState, closeConfig }) => {
    const [columnConfig, setColumnConfig] = useState<ColumnConfig>(
        rxConfig?.columns_config && Object.keys(rxConfig?.columns_config).length > 0
            ? rxConfig?.columns_config
            : DEFAULT_CONFIG_ELEMENT_IN_DOUBLE_COLUMNS,
    );

    const onSave = () => {
        saveRxConfigInState({
            columns_config: columnConfig,
        });

        closeConfig();
    };

    return (
        <ModalDialog>
            <Hotkeys keyName="esc" onKeyDown={closeConfig}>
                <motion.div
                    initial={{ opacity: 0 }}
                    exit={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{ zIndex: 20 }}
                    transition={{ duration: 0.2, ease: 'linear' }}
                    className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-lato"
                >
                    <div className="modal-overlay" onClick={closeConfig}></div>
                    <div
                        className="modal-content w-33p h-3/4 flex flex-col overflow-auto hide-scrollbar"
                        style={{ borderRadius: '1rem' }}
                    >
                        <div className="flex h-full">
                            <div className="w-50p space-y-16 h-full">
                                <div className="font-700 pt-16 text-16 text-center">Left Panel</div>
                                <div className="min-h-1 bg-darwin-neutral-500 hw-full"></div>
                                <div className="px-16">
                                    <Checkbox.Group
                                        className="space-y-8"
                                        value={columnConfig?.left}
                                        onChange={(values) => {
                                            if (values.length > 3) {
                                                notification.error({
                                                    message: 'Maximum number of columns reached',
                                                    duration: 5,
                                                    placement: 'topRight',
                                                });

                                                return;
                                            }

                                            setColumnConfig((state) => ({
                                                ...state,
                                                left: values as string[],
                                            }));
                                        }}
                                    >
                                        {doubleColumnConfig.map((column) => {
                                            return (
                                                <Col span={8}>
                                                    <Checkbox
                                                        value={column.id}
                                                        className="whitespace-nowrap select-none"
                                                        disabled={
                                                            columnConfig?.right?.find(
                                                                (id) => id === column.id,
                                                            )
                                                                ? true
                                                                : false
                                                        }
                                                    >
                                                        {column.name}
                                                    </Checkbox>
                                                </Col>
                                            );
                                        })}
                                    </Checkbox.Group>
                                </div>
                            </div>
                            <div className="min-w-1 bg-darwin-neutral-500 00 h-full"></div>
                            <div className="w-50p space-y-16 h-full">
                                <div className="font-700 pt-16 text-16 text-center">
                                    Right Panel
                                </div>
                                <div className="min-h-1 bg-darwin-neutral-500 hw-full"></div>
                                <div className="px-16">
                                    <Checkbox.Group
                                        className="space-y-8"
                                        value={columnConfig?.right}
                                        onChange={(values) => {
                                            if (values.length > 3) {
                                                notification.error({
                                                    message: 'Maximum number of columns reached',
                                                    duration: 5,
                                                    placement: 'topRight',
                                                });
                                                return;
                                            }
                                            setColumnConfig((state) => ({
                                                ...state,
                                                right: values as string[],
                                            }));
                                        }}
                                    >
                                        {doubleColumnConfig.map((column) => {
                                            return (
                                                <Col span={8}>
                                                    <Checkbox
                                                        value={column.id}
                                                        className="whitespace-nowrap select-none"
                                                        disabled={
                                                            columnConfig?.left?.find(
                                                                (id) => id === column.id,
                                                            )
                                                                ? true
                                                                : false
                                                        }
                                                    >
                                                        {column.name}
                                                    </Checkbox>
                                                </Col>
                                            );
                                        })}
                                    </Checkbox.Group>
                                </div>
                            </div>
                        </div>
                        <div className="min-h-1 bg-darwin-neutral-500 hw-full"></div>
                        <div className="px-16 py-8 flex justify-between">
                            <button
                                onClick={closeConfig}
                                className="flex px-24 py-6 secondary-btn btn--small cursor-pointer items-center"
                            >
                                Close
                            </button>
                            <button
                                onClick={onSave}
                                className="flex px-24 py-6 primary-btn btn--small cursor-pointer items-center"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </motion.div>
            </Hotkeys>
        </ModalDialog>
    );
};

export default DoubleColumnConfigModal;

export const doubleColumnConfig = [
    { id: 'vitals', name: 'Vitals' },
    { id: 'symptoms', name: 'Chief Complaints' },
    { id: 'diagnosis', name: 'Differential Diagnosis' },
    { id: 'labTests', name: 'Prescribed Lab Tests' },
    { id: 'labVitals', name: 'Investigative Readings' },
    { id: 'examinations', name: 'Examination Findings' },
    { id: 'prescriptionNotes', name: 'Notes' },
    { id: 'refer', name: 'Refer a doctor' },
    { id: 'medications', name: 'Medications' },
];
